import React, { useState, useEffect, useRef } from 'react';
import { searchProducts, fetchVariantGroups, fetchActivePromotions } from '../lib/api';
import { getOrgId } from '../utils/tenantBoot';
import ProductCard from './ProductCard';
import KioskSearchKeyboard from './KioskSearchKeyboard';

const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * Search Overlay Component
 * Full-page search interface with live results
 */
export default function SearchOverlay({ isOpen, onClose, onProductSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const kiosk = isKioskMode();
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Auto-focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset when overlay closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setHasSearched(false);
      setLoading(false);
      setShowKeyboard(false);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('[SearchOverlay] Searching for:', query.trim());
        
        const [productsData, variantGroupsData, promotionsData] = await Promise.all([
          searchProducts(query.trim()),
          fetchVariantGroups(getOrgId()),
          fetchActivePromotions().catch(err => {
            console.warn('[SearchOverlay] Failed to load promotions (non-critical):', err);
            return { promotions: [], groups: [], group_items: [] };
          })
        ]);
        
        console.log('[SearchOverlay] Products data:', productsData);
        console.log('[SearchOverlay] Variant groups:', variantGroupsData);
        
        // Handle different response formats
        let productsList = [];
        if (Array.isArray(productsData)) {
          productsList = productsData;
        } else if (productsData && Array.isArray(productsData.products)) {
          productsList = productsData.products;
        } else if (productsData && productsData.data && Array.isArray(productsData.data)) {
          productsList = productsData.data;
        }
        
        console.log('[SearchOverlay] Extracted products list:', productsList);
        
        const groups = variantGroupsData?.groups || [];
        
        // Build variant group map
        const productVariantMap = new Map();
        groups.forEach(group => {
          group.products.forEach(product => {
            productVariantMap.set(product.product_id, {
              group_id: group.group_id,
              group_name: group.group_name,
              attribute_name: group.attribute_name,
              variants: group.products,
              attributes: product.attributes
            });
          });
        });
        
        // Build barcode-to-promotion map (same algorithm used by CatalogView/ShelfView,
        // reused here rather than duplicated, so search results show promotion labels too)
        const barcodePromotionMap = {};
        if (promotionsData && promotionsData.promotions && promotionsData.group_items) {
          const { promotions, groups: promoGroups, group_items } = promotionsData;

          const promoMap = {};
          promotions.forEach(promo => {
            promoMap[promo.promo_id] = promo;
          });

          const groupMap = {};
          (promoGroups || []).forEach(group => {
            const key = `${group.promo_id}_${group.group_id}`;
            groupMap[key] = group;
          });

          group_items.forEach(item => {
            if (item.barcode) {
              const promo = promoMap[item.promo_id];
              const rewardGroupKey = Object.keys(groupMap).find(key =>
                key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'REWARD'
              );
              const qualifierGroupKey = Object.keys(groupMap).find(key =>
                key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'QUALIFIER'
              );

              if (promo && rewardGroupKey) {
                const rewardGroup = groupMap[rewardGroupKey];
                const qualifierGroup = qualifierGroupKey ? groupMap[qualifierGroupKey] : null;

                let promoText = promo.name;

                if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PRICE_OVERRIDE') {
                  promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
                } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'MONEY_OFF') {
                  promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
                } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PERCENT_OFF') {
                  promoText = `Buy ${qualifierGroup.min_qty} Get ${rewardGroup.reward_value}% Off`;
                } else if (rewardGroup.reward_type === 'MONEY_OFF') {
                  promoText = `£${rewardGroup.reward_value} Off`;
                } else if (rewardGroup.reward_type === 'PERCENT_OFF') {
                  promoText = `${rewardGroup.reward_value}% Off`;
                }

                barcodePromotionMap[item.barcode] = {
                  text: promoText,
                  promo_id: promo.promo_id,
                  promo_name: promo.name,
                  group_id: item.group_id,
                  min_qty: qualifierGroup?.min_qty || 1,
                  reward_type: rewardGroup.reward_type,
                  reward_value: rewardGroup.reward_value
                };
              }
            }
          });
        }

        // Enrich products with variant data and promotion data
        const enrichedProducts = productsList.map(product => {
          const variantData = productVariantMap.get(product.product_id);
          const promotion = product.barcode ? (barcodePromotionMap[product.barcode] || null) : null;
          return {
            ...product,
            variantData: variantData || null,
            promotion
          };
        });
        
        console.log('[SearchOverlay] Final enriched products:', enrichedProducts);
        setResults(enrichedProducts);
        setHasSearched(true);
      } catch (err) {
        console.error('Search error:', err);
        setResults([]);
        setHasSearched(true);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setShowKeyboard(false);
    onClose();
  };

  const handleProductClick = (product) => {
    onProductSelect(product);
    handleClose(); // Close search after selecting product
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay">
      {/* Backdrop */}
      <div className="search-backdrop" onClick={handleClose}></div>

      {/* Search Content */}
      <div className="search-content">
        {/* Kiosk-only on-screen keyboard, anchored above the search bar so it
            never covers the results below - website/desktop never renders this. */}
        {kiosk && showKeyboard && (
          <KioskSearchKeyboard
            value={query}
            onChange={setQuery}
            onClose={() => setShowKeyboard(false)}
          />
        )}

        {/* Search Input Area */}
        <div className="search-header">
          <div className="search-input-wrapper">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search for products..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => { if (kiosk) setShowKeyboard(true); }}
              inputMode={kiosk ? 'none' : undefined}
              autoComplete="off"
            />
            {query && (
              <button className="search-clear" onClick={() => setQuery('')}>
                ✕
              </button>
            )}
          </div>
          <button className="search-close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Search Results */}
        <div className="search-results">
          {/* Loading State */}
          {loading && (
            <div className="search-message">
              <div className="loading-spinner"></div>
              <p>Searching...</p>
            </div>
          )}

          {/* Empty State - Before Search */}
          {!loading && !hasSearched && (
            <div className="search-message">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ opacity: 0.3, marginBottom: 16 }}>
                <path d="M21 38a17 17 0 1 0 0-34 17 17 0 0 0 0 34zM42 42l-9-9" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
              </svg>
              <p>Start typing to search for products...</p>
            </div>
          )}

          {/* No Results */}
          {!loading && hasSearched && results.length === 0 && (
            <div className="search-message">
              <p>No products found for "<strong>{query}</strong>"</p>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: 8 }}>
                Try different keywords or check spelling
              </p>
            </div>
          )}

          {/* Results Grid */}
          {!loading && results.length > 0 && (
            <>
              <div className="search-results-header">
                <p>{results.length} {results.length === 1 ? 'product' : 'products'} found</p>
              </div>
              <div className="search-results-grid">
                {results.map((product) => (
                  <div
                    key={product.sku_id}
                    onClick={() => handleProductClick(product)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="product-card" data-product="true">
                    <ProductCard product={product} hideButton />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
